/**
 * Microsoft 365 OAuth Service
 * Handles M365 OAuth 2.0 authentication flow
 */

import tokenManager from "../api/token-manager";
import { TokenResponse } from "../../types/api";
import { isCapacitor } from "../../lib/platform";
import { getNativeRedirectUri, openOAuthUrl } from "./native-oauth";

const M365_CLIENT_ID =
  import.meta.env.VITE_M365_CLIENT_ID || "f64d0327-4597-45d6-9eb5-2e70f5f91959";
const M365_TENANT = import.meta.env.VITE_M365_TENANT || "common";
const WEB_REDIRECT_URI =
  import.meta.env.VITE_M365_REDIRECT_URI ||
  "http://localhost:3000/auth/m365/callback";

export class M365OAuthService {
  /**
   * Get the appropriate redirect URI based on platform
   */
  static getRedirectUri(): string {
    if (isCapacitor()) {
      return getNativeRedirectUri("m365");
    }
    return WEB_REDIRECT_URI;
  }

  /**
   * Initiate M365 OAuth login
   * On web: redirects to Microsoft consent screen
   * On native: opens in-app browser with custom scheme redirect
   */
  static async syncFromExtension(
    m365AccessToken: string,

    user: any,
  ): Promise<{ user: any; token: string }> {
    // Store the tokens and user data directly since the extension
    // already did the backend token exchange for us.
    const API_URL =
      import.meta.env.VITE_API_URL || "https://api.trailblazerops.com";

    // 1. Fetch a new JWT specifically for the React web app
    const response = await fetch(`${API_URL}/auth/m365/token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${m365AccessToken}`,
        "Content-Type": "application/json",
        "X-App-Source": "react", // <-- Forces a "react" fingerprint
      },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Sync failed" }));
      throw new Error(error.message || error.error || "Authentication failed");
    }

    const data = await response.json();

    const userData = {
      id: data.user?.id || user.id || "",
      email: user.email,
      name: user.name,
      picture: user.picture || user.profileImage,
    };

    tokenManager.setTokens({
      accessToken: data.token,
      provider: "m365",
      user: userData,
      oauthAccessToken: m365AccessToken,
      rawUserInfo: user,
    });

    console.log(
      "M365 Token and user data synced from extension. User:",
      user.name,
      user.email,
    );

    return { user, token: data.token };
  }

  static async initiateLogin(): Promise<void> {
    const redirectUri = this.getRedirectUri();
    const authUrl = new URL(
      `https://login.microsoftonline.com/${M365_TENANT}/oauth2/v2.0/authorize`,
    );

    authUrl.searchParams.set("client_id", M365_CLIENT_ID);
    authUrl.searchParams.set("redirect_uri", redirectUri);
    authUrl.searchParams.set("response_type", "id_token token");
    authUrl.searchParams.set("response_mode", "fragment");
    authUrl.searchParams.set("scope", "openid email profile User.Read");
    authUrl.searchParams.set("nonce", this.generateNonce());
    authUrl.searchParams.set("state", this.generateState());

    // Store state in localStorage for validation (more reliable than sessionStorage)
    const state = authUrl.searchParams.get("state");
    if (state) {
      localStorage.setItem("oauth_state_m365", state);
    }

    // On native, open in-app browser; on web, redirect
    const handled = await openOAuthUrl(authUrl.toString());
    if (!handled) {
      window.location.href = authUrl.toString();
    }
  }

  /**
   * Handle OAuth callback and exchange access token for JWT
   * Uses same backend endpoint as Chrome extension (/auth/m365/token)
   * @param hashParams URL hash parameters from redirect
   */
  static async handleCallback(
    hashParams: URLSearchParams,
  ): Promise<TokenResponse> {
    // Extract tokens from hash
    const accessToken = hashParams.get("access_token");
    const state = hashParams.get("state");

    if (!accessToken) {
      throw new Error("Missing access token in OAuth callback");
    }

    // Validate state (skip if state is missing from both sides)
    const storedState = localStorage.getItem("oauth_state_m365");

    if (state && storedState && state !== storedState) {
      console.error(
        "M365 State mismatch! Received:",
        state,
        "Stored:",
        storedState,
      );
      throw new Error("Invalid OAuth state");
    }

    // Clean up stored state
    localStorage.removeItem("oauth_state_m365");

    // Exchange M365 access token for backend JWT
    // Using same format as Chrome extension: access token in Authorization header
    const API_URL =
      import.meta.env.VITE_API_URL || "https://api.trailblazerops.com";
    const response = await fetch(`${API_URL}/auth/m365/token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "X-App-Source": "extension",
      },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Authentication failed" }));
      throw new Error(
        error.error || `HTTP ${response.status}: Authentication failed`,
      );
    }

    const data = await response.json();

    if (!data.token) {
      throw new Error("No JWT token received from server");
    }

    if (!data.user) {
      throw new Error("No user data received from server");
    }

    // Store JWT token, user data, and M365 access token
    tokenManager.setTokens({
      accessToken: data.token,
      provider: "m365",
      user: {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        picture: data.user.picture,
      },
      oauthAccessToken: accessToken,
    });

    console.log(
      "M365 Token and user data stored. User:",
      data.user.name,
      data.user.email,
    );
    console.log(
      "Stored in localStorage: jwt_token, user_data, m365AccessToken",
    );

    return {
      jwt_token: data.token,
      user: data.user,
    } as TokenResponse;
  }

  /**
   * Generate random nonce for OAuth security
   */
  private static generateNonce(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  /**
   * Generate random state for CSRF protection
   */
  private static generateState(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
}

export default M365OAuthService;
